import { Link, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { BarChartHoverLabel } from '@/components/admin/BarChartHoverLabel';
import { apiJson } from '@/lib/api';
import type {
  AdminDripCatalogEntry,
  AdminDripMetricsResponse,
  AdminDripSendsResponse,
} from '@/lib/types';

const CONV_PER_SEND_TOOLTIP =
  'Conversions ÷ sends in the selected range. Directional yield; conversions and sends use different grain — see catalog notes.';

type Props = {
  campaignId: string;
  catalogEntry: AdminDripCatalogEntry | undefined;
  range: '7d' | '30d' | '90d' | 'all';
};

function pct(part: number, whole: number): string {
  if (whole <= 0) return '—';
  return `${((100 * part) / whole).toFixed(1)}%`;
}

/** Sum sends per calendar day for a simple bar chart */
function dailyTotalsByDate(daily: AdminDripMetricsResponse['daily_sends']): { date: string; sends: number }[] {
  const m = new Map<string, number>();
  for (const row of daily) {
    const d = row.date.slice(0, 10);
    m.set(d, (m.get(d) ?? 0) + row.sends);
  }
  return [...m.entries()]
    .map(([date, sends]) => ({ date, sends }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function CampaignDetailDrawer({ campaignId, catalogEntry, range }: Props) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useSearch({ from: '/admin/drip-campaigns' });

  const metricsQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'metrics', campaignId, range],
    queryFn: () =>
      apiJson<AdminDripMetricsResponse>(
        getToken,
        `/v1/admin/drip-campaigns/${encodeURIComponent(campaignId)}/metrics?range=${range}`
      ),
    enabled: !!campaignId,
  });

  const sendsQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'sends', campaignId, range],
    queryFn: () =>
      apiJson<AdminDripSendsResponse>(
        getToken,
        `/v1/admin/drip-campaigns/${encodeURIComponent(campaignId)}/sends?limit=50&offset=0`
      ),
    enabled: !!campaignId,
  });

  const eligQuery = useQuery({
    queryKey: ['admin', 'drip-campaigns', 'eligibility', campaignId],
    queryFn: () =>
      apiJson<{ id: string; eligible_now: number | null; description: string }>(
        getToken,
        `/v1/admin/drip-campaigns/${encodeURIComponent(campaignId)}/eligibility`
      ),
    enabled: !!campaignId,
  });

  const series = useMemo(
    () => dailyTotalsByDate(metricsQuery.data?.daily_sends ?? []),
    [metricsQuery.data?.daily_sends]
  );
  const maxS = useMemo(() => Math.max(1, ...series.map((s) => s.sends)), [series]);
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);

  const chartWidth = Math.max(series.length * 12, 120);
  const chartHeight = 48;
  const chartPadY = 4;
  const barSlotW = 12;
  const barW = 10;
  const innerH = chartHeight - chartPadY * 2;
  const hoveredBar =
    hoveredBarIndex !== null && series[hoveredBarIndex]
      ? (() => {
          const pt = series[hoveredBarIndex];
          const h = (pt.sends / maxS) * innerH;
          const x = hoveredBarIndex * barSlotW + 1;
          return { cx: x + barW / 2, y: chartHeight - chartPadY - h, pt };
        })()
      : null;

  const close = () => {
    navigate({
      to: pathname,
      search: { ...search, campaign: undefined },
    });
  };

  const m = metricsQuery.data;
  const c = catalogEntry;

  return (
    <div
      className='fixed inset-0 z-50 flex justify-end bg-black/30'
      role='presentation'
      onClick={close}
    >
      <div
        className='h-full w-full max-w-2xl overflow-y-auto bg-nova-card shadow-xl border-l border-nova-border p-4'
        role='dialog'
        aria-modal
        aria-labelledby='drip-drawer-title'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-start justify-between gap-3'>
          <div>
            <h2 id='drip-drawer-title' className='text-xl font-semibold text-nova-foreground'>
              {c?.name ?? campaignId}
            </h2>
            <p className='text-xs font-mono text-nova-muted-foreground mt-1'>{campaignId}</p>
          </div>
          <button
            type='button'
            onClick={close}
            className='rounded-md px-3 py-1.5 text-sm text-nova-muted-foreground border border-nova-input hover:bg-nova-accent'
          >
            Close
          </button>
        </div>

        {c && (
          <div className='mt-4 flex flex-wrap gap-2 text-xs'>
            <span className='rounded-md bg-nova-muted px-2 py-0.5 capitalize'>{c.goal}</span>
            {c.channels.map((ch) => (
              <span key={ch} className='rounded-md bg-sky-50 text-sky-800 px-2 py-0.5'>
                {ch}
              </span>
            ))}
            {!c.enabled && (
              <span className='rounded-md bg-amber-100 text-amber-900 px-2 py-0.5'>Disabled</span>
            )}
            {!c.tracking_enabled && (
              <span
                className='rounded-md bg-slate-100 text-slate-800 px-2 py-0.5'
                title='Sent with mailgun_tracking=False for deliverability — opens are not recorded in DB.'
              >
                Tracking off
              </span>
            )}
          </div>
        )}

        {c?.notes && <p className='mt-3 text-sm text-nova-muted-foreground'>{c.notes}</p>}

        <div className='mt-4 rounded-lg border border-nova-border bg-nova-muted px-4 py-3 text-sm'>
          <span className='font-semibold text-nova-foreground'>Eligible now: </span>
          {eligQuery.isLoading ? (
            '…'
          ) : eligQuery.data?.eligible_now == null ? (
            '—'
          ) : (
            <span className='tabular-nums font-semibold'>{eligQuery.data.eligible_now}</span>
          )}
          {eligQuery.data?.description && (
            <p className='text-xs text-nova-muted-foreground mt-1'>{eligQuery.data.description}</p>
          )}
        </div>

        {metricsQuery.isLoading ? (
          <div className='flex justify-center py-12 mt-6'>
            <div className='animate-spin h-8 w-8 border-2 border-nova-primary border-t-transparent rounded-full' />
          </div>
        ) : metricsQuery.isError ? (
          <p className='mt-6 text-red-600'>Could not load metrics.</p>
        ) : m ? (
          <>
            <div className='mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center'>
              <div className='rounded-lg border border-nova-border bg-nova-card p-3'>
                <p className='text-[10px] font-semibold uppercase text-nova-muted-foreground'>Sends</p>
                <p className='text-lg font-semibold tabular-nums'>{m.total_sends}</p>
              </div>
              <div className='rounded-lg border border-nova-border bg-nova-card p-3'>
                <p className='text-[10px] font-semibold uppercase text-nova-muted-foreground'>Opens</p>
                <p className='text-lg font-semibold tabular-nums'>
                  {m.total_opens == null ? '—' : m.total_opens}
                </p>
              </div>
              <div className='rounded-lg border border-nova-border bg-nova-card p-3'>
                <p className='text-[10px] font-semibold uppercase text-nova-muted-foreground'>Conversions</p>
                <p className='text-lg font-semibold tabular-nums'>{m.conversions}</p>
              </div>
              <div
                className='rounded-lg border border-nova-border bg-nova-card p-3 cursor-help'
                title={CONV_PER_SEND_TOOLTIP}
              >
                <p className='text-[10px] font-semibold uppercase text-nova-muted-foreground'>Conv. % of sends</p>
                <p className='text-lg font-semibold tabular-nums'>
                  {m.conversion_rate != null && m.total_sends > 0
                    ? `${(100 * m.conversion_rate).toFixed(1)}%`
                    : '—'}
                </p>
              </div>
            </div>

            {series.length > 0 && (
              <div className='mt-6'>
                <h3 className='text-sm font-semibold text-nova-foreground mb-2'>Sends by day</h3>
                <svg
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  className='w-full h-12 text-nova-primary'
                  onMouseLeave={() => setHoveredBarIndex(null)}
                >
                  {series.map((pt, i) => {
                    const h = (pt.sends / maxS) * innerH;
                    const x = i * barSlotW + 1;
                    const y = chartHeight - chartPadY - h;
                    const isHovered = hoveredBarIndex === i;
                    return (
                      <g key={pt.date}>
                        <rect
                          x={x}
                          y={y}
                          width={barW}
                          height={h}
                          fill='currentColor'
                          opacity={hoveredBarIndex != null && !isHovered ? 0.45 : 1}
                          rx={1}
                        />
                        <rect
                          x={i * barSlotW}
                          y={chartPadY}
                          width={barSlotW}
                          height={innerH}
                          fill='transparent'
                          onMouseEnter={() => setHoveredBarIndex(i)}
                        />
                      </g>
                    );
                  })}
                  {hoveredBar ? (
                    <BarChartHoverLabel
                      x={hoveredBar.cx}
                      barTopY={hoveredBar.y}
                      minX={12}
                      maxX={chartWidth - 12}
                      minY={8}
                      fontSize={9}
                      text={`${hoveredBar.pt.date} · ${hoveredBar.pt.sends.toLocaleString()} sends`}
                    />
                  ) : null}
                </svg>
                <p className='text-[10px] text-nova-muted-foreground mt-1'>
                  Open rate:{' '}
                  {m.open_rate == null ? '—' : `${(100 * m.open_rate).toFixed(1)}%`} · Clicks are not persisted in
                  DB.
                </p>
              </div>
            )}

            <div className='mt-6 overflow-hidden rounded-lg border border-nova-border'>
              <div className='px-4 py-2 bg-nova-muted border-b border-nova-border'>
                <h3 className='text-sm font-semibold text-nova-foreground'>Steps</h3>
              </div>
              <table className='w-full text-sm'>
                <thead className='bg-nova-muted text-left text-xs uppercase text-nova-muted-foreground'>
                  <tr>
                    <th className='px-4 py-2'>Step</th>
                    <th className='px-4 py-2'>Channel</th>
                    <th className='px-4 py-2 text-right'>Sends</th>
                    <th className='px-4 py-2 text-right'>Opens</th>
                    <th className='px-4 py-2 text-right'>Open %</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-nova-border'>
                  {m.step_breakdown.map((row) => (
                    <tr key={`${row.step_key}-${row.channel}`} className='hover:bg-nova-accent'>
                      <td className='px-4 py-2 font-mono text-xs'>{row.step_key}</td>
                      <td className='px-4 py-2'>{row.channel}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{row.sends}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{row.opens ?? '—'}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>
                        {row.opens == null ? '—' : pct(row.opens, row.sends)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        <div className='mt-8'>
          <h3 className='text-sm font-semibold text-nova-foreground mb-2'>Recent sends</h3>
          {sendsQuery.isLoading ? (
            <p className='text-nova-muted-foreground text-sm'>Loading…</p>
          ) : sendsQuery.isError ? (
            <p className='text-red-600 text-sm'>Could not load sends.</p>
          ) : (
            <div className='overflow-x-auto rounded-lg border border-nova-border'>
              <table className='w-full text-sm'>
                <thead className='bg-nova-muted text-left text-xs uppercase text-nova-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2'>When</th>
                    <th className='px-3 py-2'>Step</th>
                    <th className='px-3 py-2'>Recipient</th>
                    <th className='px-3 py-2'>Opened</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-nova-border'>
                  {(sendsQuery.data?.rows ?? []).map((r) => (
                    <tr key={r.id} className='hover:bg-nova-accent'>
                      <td className='px-3 py-2 text-xs text-nova-muted-foreground whitespace-nowrap'>
                        {new Date(r.sent_at).toLocaleString()}
                      </td>
                      <td className='px-3 py-2 font-mono text-xs'>{r.step_key}</td>
                      <td className='px-3 py-2 max-w-[200px] truncate'>
                        {r.user_id != null && c?.keyed_by !== 'prospect_id' ? (
                          <Link
                            to='/admin/users/$userId'
                            params={{ userId: String(r.user_id) }}
                            search={{ hid: undefined }}
                            className='text-nova-primary font-medium hover:underline'
                          >
                            {r.user_label?.trim() ? r.user_label : `User #${r.user_id}`}
                          </Link>
                        ) : r.invite_id != null ? (
                          <span title='SMS to invitee; inviter shown'>Invite #{r.invite_id}</span>
                        ) : r.prospect_id != null ? (
                          <span className='text-nova-foreground'>{r.user_label ?? `Prospect #${r.prospect_id}`}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className='px-3 py-2 text-xs'>
                        {r.opened_at ? (
                          <span className='text-green-700'>Yes</span>
                        ) : c?.tracking_enabled ? (
                          '—'
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
